import * as React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, Grid, TextField, Autocomplete, FormControlLabel, Switch, AppBar, Toolbar, Typography, FormControl, FormLabel, FormGroup, Checkbox, CircularProgress, Box, Tabs, Tab } from "@mui/material"
import { useClientesService } from "../../../../../../use-cases/clientes/useClientesService"
import { useAppSelector } from "../../../../../hooks/store-hooks"
import { useEffect, useState } from "react"
import CloseIcon from '@mui/icons-material/Close';
import { useVendedorService } from "../../../../../../use-cases/vendedor";
import { useProdutoService } from "../../../../../../use-cases/produto";
import { useTipoConsumoService } from "../../../../../../use-cases/tipo-consumo";
import { formatCnpj } from '../../../../../../utils';
import { ITipoUsuario, PlanosVelesTabRef } from '../../../../../../application/models';
import { PlanosVelesTab } from '../../../../../components/planos-veles/PlanosVelesTab';
import { NotaFiscalTab, NotaFiscalTabRef } from '../../../../../components/nota-fiscal/NotaFiscalTab';
import { ContratoTab } from '../../../../../components/contrato/ContratoTab';
import { HistoricoPlanosTab } from '../../../../../components/planos-veles/HistoricoPlanosTab';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))

export const FormEditCliente = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [tabValue, setTabValue] = React.useState(0);
  const [camposComErro, setCamposComErro] = React.useState<Set<string>>(new Set());
  const [planosVelesCarregado, setPlanosVelesCarregado] = React.useState(false);
  const [notaFiscalCarregado, setNotaFiscalCarregado] = React.useState(false);
  const editClienteForm = useAppSelector((state) => state.clientes.editClienteForm);
  const planosVelesRef = React.useRef<PlanosVelesTabRef>(null);
  const notaFiscalRef = React.useRef<NotaFiscalTabRef>(null);
  const user = useAppSelector((state) => state.user.currentUser);
  const hasLoadedData = React.useRef(false);
  const isLoadingData = React.useRef(false);
  const lastClienteIdRef = React.useRef<string | null>(null);

  const {
    setEditClienteFormNomeFantasia,
    setEditClienteFormRazaoSocial,
    setEditClienteFormStatus,
    setEditClienteFormVendedorId,
    setEditClienteFormGerarLoteFinanceiro,
    setEditClienteOpenForm,
    setProdutoClienteByIdCliente,
    removeTipoConsumoEditClienteFormFields,
    setVincularTipoConsumoEditClienteFormFields,
    removeProdutoEditClienteFormFields,
    handleTabChange,
    handleEditClienteSubmit,
    updateCliente,
    setEditClienteFormCnpj,
    setEditClienteFormLogin
  } = useClientesService()

  const vendedores = useAppSelector((state) => state.vendedor)
  const { produtosTipoConsumo } = useAppSelector((state) => state.produto)
  const [expanded, setExpanded] = React.useState<{ [key: string]: boolean }>({})
  const [searchTerms, setSearchTerms] = React.useState<{ [key: number]: string }>({})


  const { setVendedores } = useVendedorService()
  const { setAllProdutosAndTipoConsumo } = useTipoConsumoService()

  useEffect(() => {
    const loadData = async () => {
      // Verificar se já está carregando ou se já foi carregado para este cliente
      if (isLoadingData.current) return;

      const currentClienteId = editClienteForm.fields.id || null;
      if (hasLoadedData.current && lastClienteIdRef.current === currentClienteId) {
        return;
      }

      isLoadingData.current = true;
      hasLoadedData.current = true;
      lastClienteIdRef.current = currentClienteId;

      try {
        setIsLoading(true);

        const promises: Promise<any>[] = [];

        promises.push(setAllProdutosAndTipoConsumo(Number(editClienteForm.fields.id)).catch(() => {
        }));


        promises.push(setVendedores().catch(() => {
        }));

        if (promises.length > 0) {
          await Promise.allSettled(promises);
        }

        if (editClienteForm.fields.id) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }

        setPlanosVelesCarregado(true);
        setNotaFiscalCarregado(true);

        setTimeout(() => {
          setIsLoading(false);
        }, 100);
      } catch (error) {
        setIsLoading(false);
        hasLoadedData.current = false;
        lastClienteIdRef.current = null;
      } finally {
        isLoadingData.current = false;
      }
    };

    loadData();

    return () => {
      // Resetar apenas se o cliente mudou
      const currentClienteId = editClienteForm.fields.id || null;
      if (lastClienteIdRef.current !== currentClienteId) {
        hasLoadedData.current = false;
        isLoadingData.current = false;
        lastClienteIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editClienteForm.fields.id]);

  const handleExpandClick = (key: string) => {
    setExpanded((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const handleClose = () => {
    setEditClienteOpenForm({
      open: false
    })
  }

  const handleTabChangeWrapper = async (event: React.SyntheticEvent, newValue: number) => {
    try {
      const result = await handleTabChange(tabValue, newValue, planosVelesRef, notaFiscalRef);

      if (typeof result === 'number') {
        setTabValue(result);
      } else {
        setTabValue(newValue);
      }
    } catch (error) {
      setTabValue(newValue);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const isVelesUser = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS
  const isTecnologiaDaInformacao = user?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO
  const isSupervisorComercial = user?.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL
  const isComercial = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL
  const isExperienciaCliente = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE
  const isCliente = user?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE

  const canEdit = isVelesUser || isTecnologiaDaInformacao
  const canView = isVelesUser || isTecnologiaDaInformacao || isSupervisorComercial || isComercial || isExperienciaCliente || isCliente;

  return (
    <>
      <Dialog
        open={true}
        onClose={handleClose}
        fullScreen
        scroll={'paper'}
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
        PaperProps={{
          component: 'form',
          onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
            await handleEditClienteSubmit(
              event,
              planosVelesRef,
              notaFiscalRef,
              setTabValue,
              setCamposComErro
            );
          },
        }}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1 }} textTransform={"uppercase"} fontWeight={"bold"} variant="h6" component="div">
              {canEdit ? 'Editar Cliente' : 'Visualizar Cliente'}
            </Typography>
            <Button
              color="inherit"
              startIcon={<CloseIcon />}
              onClick={handleClose}
              aria-label="close"
            >
              Fechar
            </Button>
          </Toolbar>
        </AppBar>
        <DialogContent dividers={true}>
          <DialogContentText id="scroll-dialog-description" tabIndex={-1}></DialogContentText>
          <Box p={3}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChangeWrapper} aria-label="formulário cliente">
                <Tab label="Dados do Cliente" />
                <Tab label="Produtos" />
                {canView && <Tab label="Planos" />}
                {canView && <Tab label="Histórico de Planos" />}
                {canView && <Tab label="Contrato" />}
                {canEdit && <Tab label="Nota Fiscal" />}
              </Tabs>
            </Box>

            {tabValue === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    value={formatCnpj(editClienteForm.fields.cnpj || '')}
                    error={camposComErro.has('cnpj') || Boolean(editClienteForm.touched && editClienteForm.errors.find(item => item.key === 'cnpj'))}
                    helperText={
                      camposComErro.has('cnpj')
                        ? 'Campo obrigatório'
                        : editClienteForm.errors.find(item => item.key === 'cnpj')?.message
                    }
                    name="cnpj"
                    fullWidth
                    id="cnpj"
                    label="CNPJ"
                    autoFocus
                    disabled={!isVelesUser && !isTecnologiaDaInformacao}
                    onChange={(e) => {
                      if (!isVelesUser && !isTecnologiaDaInformacao) return;
                      setEditClienteFormCnpj(e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={!canEdit}
                    value={editClienteForm.fields.razaoSocial || ''}
                    onChange={(e) => {
                      if (!canEdit) return;
                      setEditClienteFormRazaoSocial(e.target.value);
                    }}
                    error={camposComErro.has('razaoSocial') || Boolean(editClienteForm.touched && editClienteForm.errors.find(item => item.key === 'razaoSocial'))}
                    helperText={
                      camposComErro.has('razaoSocial')
                        ? 'Campo obrigatório'
                        : editClienteForm.errors.find(item => item.key === 'razaoSocial')?.message
                    }
                    name="razaoSocial"
                    fullWidth
                    id="razaoSocial"
                    label="Razão Social"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={!canEdit}
                    value={editClienteForm.fields.nomeFantasia || ''}
                    onChange={(e) => {
                      if (!canEdit) return;
                      setEditClienteFormNomeFantasia(e.target.value);
                    }}
                    error={camposComErro.has('nomeFantasia') || Boolean(editClienteForm.touched && editClienteForm.errors.find(item => item.key === 'nomeFantasia'))}
                    helperText={
                      camposComErro.has('nomeFantasia')
                        ? 'Campo obrigatório'
                        : editClienteForm.errors.find(item => item.key === 'nomeFantasia')?.message
                    }
                    name="nomeFantasia"
                    fullWidth
                    id="nomeFantasia"
                    label="Nome Fantasia"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={!canEdit}
                    value={editClienteForm.fields.login || ''}
                    onChange={(e) => {
                      if (!isVelesUser) return;
                      setEditClienteFormLogin(e.target.value);
                    }}
                    error={camposComErro.has('login') || Boolean(editClienteForm.touched && editClienteForm.errors.find(item => item.key === 'login'))}
                    helperText={
                      camposComErro.has('login')
                        ? 'Campo obrigatório'
                        : editClienteForm.errors.find(item => item.key === 'login')?.message
                    }
                    name="login"
                    fullWidth
                    id="login"
                    label="Login"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    disabled={!canEdit}
                    options={vendedores.vendedores}
                    getOptionLabel={(option) => (option ? option.LoginUsuario : '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Vendedor"
                        fullWidth
                        error={camposComErro.has('idVendedor') || Boolean(
                          editClienteForm.touched &&
                          editClienteForm.errors.find((item) => item.key === 'idVendedor')
                        )}
                        helperText={
                          camposComErro.has('idVendedor')
                            ? 'Campo obrigatório'
                            : editClienteForm.errors.find((item) => item.key === 'idVendedor')?.message
                        }
                        sx={{ marginBottom: 2 }}
                      />
                    )}
                    onChange={(event, newValue) => {
                      if (!canEdit) return;
                      if (newValue) {
                        setEditClienteFormVendedorId(newValue.IdUsuario);
                      }
                    }}
                    value={vendedores.vendedores.find(
                      (vendedor) => Number(vendedor.IdUsuario) === Number(editClienteForm.fields.idVendedor)
                    ) || null}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch
                    disabled={!canEdit}
                    checked={Boolean(editClienteForm.fields.Ativo)}
                    onChange={(e) => {
                      if (!canEdit) return;
                      setEditClienteFormStatus(e.target.checked)
                    }}
                    name="status"
                    color="primary"
                  />} label={
                    editClienteForm.fields.Ativo ? 'Cliente ativo' : 'Cliente inativo'
                  } />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch
                    disabled={!canEdit || !editClienteForm.fields.Ativo}
                    checked={editClienteForm.fields.Ativo
                      ? Boolean(editClienteForm.fields.GerarLoteFinanceiro)
                      : false
                    }
                    onChange={(e) => {
                      if (!canEdit) return;
                      setEditClienteFormGerarLoteFinanceiro(e.target.checked)
                    }}
                    name="faturarOmie"
                    color="primary"
                  />} label="Faturar Omie" />
                </Grid>
              </Grid>
            )}

            {tabValue === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h5"
                    color="primary"
                    fontWeight="bold"
                    textTransform={"uppercase"}
                    textAlign={'center'}
                    width={'100%'}
                    my={2}
                  >
                    Produtos
                  </Typography>
                  <Card>
                    {produtosTipoConsumo.map((produtoTipoConsumo, produtoIndex) => (
                      <div key={produtoIndex} style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                disabled={!canEdit}
                                checked={editClienteForm.fields.ProdutosTiposConsumos.some(
                                  (item) => item.IdProduto == produtoTipoConsumo.produto.IdProduto
                                )}
                                onChange={(event) => {
                                  if (!canEdit) return;
                                  if (event.target.checked) {
                                    produtoTipoConsumo.tipoConsumo.forEach((tipoConsumo) => {
                                      setVincularTipoConsumoEditClienteFormFields({
                                        IdProduto: produtoTipoConsumo.produto.IdProduto,
                                        IdTipoConsumo: tipoConsumo.IdTipoConsumo
                                      });
                                    });
                                  } else {
                                    removeProdutoEditClienteFormFields({
                                      idProduto: produtoTipoConsumo.produto.IdProduto
                                    });
                                  }
                                }}
                                name={`produto-${produtoIndex}`}
                              />
                            }
                            label={produtoTipoConsumo.produto.Descricao}
                            style={{ margin: 0 }}
                          />
                          <ExpandMore
                            expand={expanded[produtoIndex] || false}
                            onClick={() => handleExpandClick(produtoIndex.toString())}
                            aria-expanded={expanded[produtoIndex] || false}
                            aria-label="show more"
                          >
                            <ExpandMoreIcon />
                          </ExpandMore>
                        </div>

                        <Collapse in={expanded[produtoIndex] || false} timeout="auto" unmountOnExit>
                          <CardContent style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                            <FormControl component="fieldset" fullWidth>
                              <FormLabel component="legend" style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                                Escolha as variáveis
                              </FormLabel>

                              <TextField
                                size="small"
                                placeholder="Pesquisar variável..."
                                variant="outlined"
                                fullWidth
                                sx={{ mb: 1 }}
                                value={searchTerms[produtoTipoConsumo.produto.IdProduto] || ''}
                                onChange={(e) => setSearchTerms(prev => ({
                                  ...prev,
                                  [produtoTipoConsumo.produto.IdProduto]: e.target.value
                                }))}
                              />

                              <FormGroup style={{ paddingLeft: '1.5rem' }}>
                                {produtoTipoConsumo.tipoConsumo
                                  .filter(tc =>
                                    !searchTerms[produtoTipoConsumo.produto.IdProduto] ||
                                    tc.Descricao.toLowerCase().includes(searchTerms[produtoTipoConsumo.produto.IdProduto].toLowerCase())
                                  )
                                  .map((tipoConsumo, tipoIndex) => (
                                    <FormControlLabel
                                      key={`produto-${produtoIndex}-tipo-${tipoIndex}`}
                                      control={
                                        <Checkbox
                                          disabled={!canEdit}
                                          checked={editClienteForm.fields.ProdutosTiposConsumos.some(
                                            (item) => item.IdProduto == produtoTipoConsumo.produto.IdProduto &&
                                              item.IdTipoConsumo == tipoConsumo.IdTipoConsumo
                                          )}
                                          onChange={(event) => {
                                            if (!canEdit) return;
                                            if (event.target.checked) {
                                              setVincularTipoConsumoEditClienteFormFields({
                                                IdProduto: produtoTipoConsumo.produto.IdProduto,
                                                IdTipoConsumo: tipoConsumo.IdTipoConsumo
                                              });
                                            } else {
                                              removeTipoConsumoEditClienteFormFields({
                                                idProduto: produtoTipoConsumo.produto.IdProduto,
                                                idTipoConsumo: tipoConsumo.IdTipoConsumo
                                              });
                                            }
                                          }}
                                          name={`produto-${produtoIndex}-tipo-${tipoIndex}`}
                                        />
                                      }
                                      label={tipoConsumo.Descricao}
                                    />
                                  ))}
                              </FormGroup>
                            </FormControl>
                          </CardContent>

                        </Collapse>
                      </div>
                    ))}
                  </Card>
                </Grid>
              </Grid>
            )}

            {planosVelesCarregado && (
              <Box sx={{ display: tabValue === 2 ? 'block' : 'none' }}>
                <PlanosVelesTab
                  key={`planos-veles-${editClienteForm.fields.id}`}
                  ref={planosVelesRef}
                  IdCliente={Number(editClienteForm.fields.id)}
                  viewMode={canEdit ? 'edit' : 'view'}
                />
              </Box>
            )}

            {notaFiscalCarregado && (
              <Box sx={{ display: tabValue === (canView ? 5 : 3) ? 'block' : 'none' }}>
                <NotaFiscalTab
                  key={`nota-fiscal-${editClienteForm.fields.id}`}
                  ref={notaFiscalRef}
                  IdCliente={Number(editClienteForm.fields.id)}
                  isEditMode={canEdit}
                  isEditing={canEdit}
                />
              </Box>
            )}

            {canView && (
              <Box sx={{ display: tabValue === 3 ? 'block' : 'none' }}>
                <HistoricoPlanosTab IdCliente={Number(editClienteForm.fields.id)} />
              </Box>
            )}

            {canView && (
              <Box sx={{ display: tabValue === 4 ? 'block' : 'none' }}>
                <ContratoTab IdCliente={Number(editClienteForm.fields.id)} isEditMode={canEdit} />
              </Box>
            )}
          </Box>
        </DialogContent>
        {canEdit && (
          <DialogActions>
            <Button
              type="submit"
              variant="contained"
            >
              Salvar
            </Button>
          </DialogActions>
        )}
      </Dialog >
    </>
  )
}